import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Form, Table } from "react-bootstrap";
import {
  listUpgradeItems,
  createUpgradeItem,
  updateUpgradeItem,
  deleteUpgradeItem,
  reorderUpgradeItems,
  isAbortError,
  errorMessage,
} from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";

// Upgrade List (requests-2026-09-02.md R3) — the stored upgrade backlog as a
// working to-do list, admins only. One-line titles expand on click to the
// full description; KS and KR drag the priority order; "Later" marks the
// nice-to-have-much-later tier. Default view is OPEN items in priority
// order; Done keeps completed items visible; any admin can make an item
// inactive, only the system owner can delete one.

const FILTERS = [
  ["open", "To do"],
  ["done", "Done"],
  ["inactive", "Inactive"],
  ["all", "All"],
];
const LATER_FILTERS = [
  ["all", "Now + Later"],
  ["now", "Hide Later"],
  ["later", "Later only"],
];
const EFFORT_BADGE = { easy: "success", medium: "warning", hard: "danger" };
const EMPTY_FORM = { title: "", description: "", category: "", effort: "medium", later: false };

function UpgradeListManager({ isOwner = false }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [laterFilter, setLaterFilter] = useState("all"); // all | now | later
  const [expanded, setExpanded] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const firstRef = useRef(null);

  function load() {
    return listUpgradeItems()
      .then(setItems)
      .catch((err) => setError(errorMessage(err, "Unable to load the upgrade list.")));
  }
  useEffect(() => {
    const controller = new AbortController();
    listUpgradeItems(controller.signal)
      .then(setItems)
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the upgrade list."));
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (editingId) firstRef.current?.focus();
  }, [editingId]);

  // One predicate for the view AND the reorder splice below, so they can
  // never disagree about which rows are on screen.
  const isVisible = (it) =>
    (filter === "all" || it.status === filter) &&
    (laterFilter === "all" || (laterFilter === "later") === !!it.later);
  const visible = items.filter(isVisible);
  const counts = items.reduce((acc, it) => ((acc[it.status] = (acc[it.status] || 0) + 1), acc), {});
  const categories = [...new Set(items.map((it) => it.category).filter(Boolean))];

  function toggleExpand(id) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function startAdd() {
    setError("");
    setForm(EMPTY_FORM);
    setEditingId("new");
  }
  function startEdit(it) {
    setError("");
    setForm({
      title: it.title || "",
      description: it.description || "",
      category: it.category || "",
      effort: it.effort || "medium",
      later: !!it.later,
    });
    setEditingId(it.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") await createUpgradeItem(form);
      else await updateUpgradeItem(editingId, form);
      setEditingId(null);
      load();
    } catch (err) {
      setError(errorMessage(err, "Unable to save."));
    }
  }
  // Later <-> Now: the nice-to-have tier, flipped from the row itself so
  // triage does not mean opening the editor.
  async function setLater(it, later) {
    setError("");
    try {
      await updateUpgradeItem(it.id, { later });
      load();
    } catch (err) {
      setError(errorMessage(err, "Unable to update."));
    }
  }
  async function setStatus(it, status) {
    setError("");
    try {
      await updateUpgradeItem(it.id, { status });
      load();
    } catch (err) {
      setError(errorMessage(err, "Unable to update."));
    }
  }
  async function remove(it) {
    if (!window.confirm(`Delete "${it.title}" for good? Inactive keeps it on record instead.`)) return;
    setError("");
    try {
      await deleteUpgradeItem(it.id);
      load();
    } catch (err) {
      setError(errorMessage(err, "Unable to delete."));
    }
  }

  // Drag-drop moves rows within the CURRENT view, but the server renumbers
  // the WHOLE list — so splice the reordered visible rows back into the full
  // list at the positions the view occupies, and send every id.
  async function handleReorder(reorderedVisible) {
    const prev = items;
    const queue = [...reorderedVisible];
    const full = items.map((it) => (isVisible(it) ? queue.shift() : it));
    setItems(full);
    try {
      await reorderUpgradeItems(full.map((it) => it.id));
    } catch (err) {
      setItems(prev);
      setError(errorMessage(err, "Unable to save the new order."));
    }
  }

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  const dragDisabled = editingId !== null;

  function editRow(key) {
    return (
      <tr key={`edit-${key}`}>
        <td />
        <td colSpan={2}>
          <Form.Control
            size="sm"
            className="mb-2"
            placeholder="One-line title"
            ref={firstRef}
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
          />
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            placeholder="Description — what it is, who asked, why it matters"
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </td>
        <td>
          <Form.Control
            size="sm"
            list="upgrade-categories"
            placeholder="Category"
            value={form.category}
            onChange={(e) => set({ category: e.target.value })}
          />
          <datalist id="upgrade-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </td>
        <td>
          <ThemedSelect size="sm" value={form.effort} onChange={(e) => set({ effort: e.target.value })}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </ThemedSelect>
          <Form.Check
            type="checkbox"
            id={`upg-later-${key}`}
            className="mt-1 small"
            label="Later"
            data-hint="Nice to have, done much later"
            checked={form.later}
            onChange={(e) => set({ later: e.target.checked })}
          />
        </td>
        <td className="text-end text-nowrap">
          <Button size="sm" className="me-2" onClick={save} disabled={!form.title.trim()}>
            {key === "new" ? "Add" : "Save"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
            Cancel
          </Button>
        </td>
      </tr>
    );
  }

  function viewRow(it, idx) {
    const open = expanded.has(it.id);
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled}>
        <td className="text-muted">{idx + 1}</td>
        <td
          role="button"
          onClick={() => toggleExpand(it.id)}
          className={it.status === "inactive" ? "text-muted" : undefined}
          data-hint={open ? "Hide description" : "Show description"}
        >
          <span className="text-muted me-2">{open ? "▾" : "▸"}</span>
          <span className={it.status === "done" ? "text-decoration-line-through" : undefined}>
            {it.title}
          </span>
          {it.later ? (
            <Badge bg="info" className="ms-2" data-hint="Nice to have, done much later">
              Later
            </Badge>
          ) : null}
          {it.status === "done" && it.completed_at ? (
            <span className="small text-muted ms-2">
              done {new Date(it.completed_at).toLocaleDateString("en-NZ")}
            </span>
          ) : null}
        </td>
        <td className="small text-muted">{it.category || "—"}</td>
        <td>
          <Badge bg={EFFORT_BADGE[it.effort] || "secondary"}>{it.effort}</Badge>
        </td>
        <td className="text-end text-nowrap">
          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(it)}>
            Edit
          </Button>
          {it.status === "open" ? (
            <>
              <Button
                variant="outline-info"
                size="sm"
                className="me-2"
                data-hint={it.later ? "Move back into the main list" : "Nice to have, done much later"}
                onClick={() => setLater(it, !it.later)}
              >
                {it.later ? "Now" : "Later"}
              </Button>
              <Button variant="outline-success" size="sm" className="me-2" onClick={() => setStatus(it, "done")}>
                Done
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className={isOwner ? "me-2" : undefined}
                data-hint="Keep the item on record without deleting it"
                onClick={() => setStatus(it, "inactive")}
              >
                Inactive
              </Button>
            </>
          ) : (
            <Button
              variant="outline-secondary"
              size="sm"
              className={isOwner ? "me-2" : undefined}
              onClick={() => setStatus(it, "open")}
            >
              Reopen
            </Button>
          )}
          {isOwner ? (
            <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>
              Delete
            </Button>
          ) : null}
        </td>
      </SortableTr>
    );
  }

  const rows = [];
  for (const [idx, it] of visible.entries()) {
    rows.push(editingId === it.id ? editRow(it.id) : viewRow(it, idx));
    if (editingId !== it.id && expanded.has(it.id)) {
      rows.push(
        <tr key={`desc-${it.id}`}>
          <td />
          <td colSpan={5} className="small" style={{ whiteSpace: "pre-wrap" }}>
            {it.description || <span className="text-muted">No description.</span>}
          </td>
        </tr>
      );
    }
  }
  if (editingId === "new") rows.push(editRow("new"));

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <h5 className="mb-0">Upgrade List</h5>
          <div className="small text-muted">
            Click a title for the detail. Drag rows to set the priority order — the order is the ranking.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ThemedSelect
            size="sm"
            style={{ width: "auto" }}
            value={laterFilter}
            onChange={(e) => setLaterFilter(e.target.value)}
            data-hint="Show or hide the Later tier"
          >
            {LATER_FILTERS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </ThemedSelect>
          <ButtonGroup size="sm">
            {FILTERS.map(([key, label]) => (
              <Button
                key={key}
                variant={filter === key ? "primary" : "outline-primary"}
                onClick={() => setFilter(key)}
              >
                {label}
                {key !== "all" ? ` (${counts[key] || 0})` : ` (${items.length})`}
              </Button>
            ))}
          </ButtonGroup>
          {editingId === null ? (
            <Button size="sm" variant="success" onClick={startAdd}>
              + Add
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <SortableRows items={visible} onReorder={handleReorder} disabled={dragDisabled}>
        <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
          <thead className="table-surface">
            <tr>
              <th style={{ width: 36 }} />
              <th style={{ width: 44 }}>#</th>
              <th>Item</th>
              <th style={{ width: 180 }}>Category</th>
              <th style={{ width: 90 }}>Effort</th>
              <th style={{ width: isOwner ? 350 : 280 }} className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {visible.length === 0 && editingId !== "new" ? (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  Nothing here.
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </SortableRows>
    </>
  );
}

export default UpgradeListManager;
